import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActiveSession, useAuth } from "../../context/AuthContext";
import { deviceLabel, fmt, Spinner } from "../core/Common";

type LoadingState = string | "ALL" | "CONTINUE" | "SWITCH" | null;

function Gate({
  current,
  others,
  onRevokeOne,
  // onRevokeAllOthers,
  onContinueHere,
  onSwitchAccount,
  loading,
}: {
  current: ActiveSession | null;
  others: ActiveSession[];
  onRevokeOne: (tokenId: string) => Promise<void>;
  // onRevokeAllOthers: () => Promise<void>;
  onContinueHere: () => Promise<void>;
  onSwitchAccount: () => Promise<void>;
  loading: LoadingState;
}) {
  return (
    <div className="min-h-[100svh] w-full bg-slate-950 text-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-slate-900/70 backdrop-blur p-6 shadow-xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            Multiple sign-ins detected
          </h1>
          <p className="text-sm text-slate-300 mt-1">
            Your account is signed in on more than one device. End other
            sessions or switch account to continue.
          </p>
        </div>

        <div className="rounded-xl  bg-slate-900 p-4 mb-4">
          <h2 className="text-sm font-semibold text-slate-200 mb-2">
            This device
          </h2>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm">
                <span className="font-medium">{deviceLabel(current)}</span>
              </div>
              <div className="text-xs text-slate-400">
                Last active: {fmt(current?.lastActiveAt)}
              </div>
            </div>
            <span className="inline-flex items-center rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-300 ring-1 ring-inset ring-emerald-500/30">
              Current session
            </span>
          </div>
        </div>

        <div className="rounded-xl  bg-slate-900 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-200">
              Other active sessions ({others.length})
            </h2>
          </div>

          {others.length === 0 ? (
            <div className="text-sm text-slate-400">
              No other sessions found.
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {others.map((session) => (
                <li
                  key={session.tokenId}
                  className="py-3 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="text-sm truncate">
                      <span className="font-medium">
                        {deviceLabel(session)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400">
                      Last active: {fmt(session.lastActiveAt)} · Expires:{" "}
                      {fmt(session.expiresAt)}
                      {session.device?.ipAddress
                        ? ` · IP: ${session.device.ipAddress}`
                        : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => onRevokeOne(session.tokenId)}
                    disabled={loading === session.tokenId}
                    className="inline-flex items-center gap-2 rounded-full ring-1 ring-inset ring-rose-700/40 bg-rose-600/10 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-600/20 disabled:opacity-50"
                  >
                    {loading === session.tokenId && <Spinner />} Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
          <button
            onClick={onSwitchAccount}
            disabled={loading === "SWITCH"}
            className="inline-flex items-center justify-center gap-2 rounded-full ring-1 ring-inset ring-slate-700 bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-700/70 disabled:opacity-50"
          >
            {loading === "SWITCH" && <Spinner />} Switch account
          </button>
          <button
            onClick={onContinueHere}
            disabled={loading === "CONTINUE" || others.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading === "CONTINUE" && <Spinner />} Logout other devices
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionLimiter({ children }: { children: React.ReactNode }) {
  const {
    currentUser,
    sessions,
    refreshSessions,
    revokeSession,
    revokeOtherSessions,
    signOut,
  } = useAuth();

  const [loading, setLoading] = useState<LoadingState>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setRefreshTick((tick) => tick + 1);
      }
    };

    const handleOnline = () => setRefreshTick((tick) => tick + 1);

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    void refreshSessions();
  }, [currentUser, refreshSessions, refreshTick]);

  const current = useMemo(
    () => sessions.find((session) => session.current) ?? null,
    [sessions]
  );

  const others = useMemo(
    () => sessions.filter((session) => !session.current),
    [sessions]
  );

  const onRevokeOne = useCallback(
    async (tokenId: string) => {
      try {
        setLoading(tokenId);
        await revokeSession(tokenId);
      } catch (error) {
        console.error(error);
        alert("Unable to revoke that session right now.");
      } finally {
        setLoading(null);
      }
    },
    [revokeSession]
  );

  // const onRevokeAllOthers = useCallback(async () => {
  //   try {
  //     setLoading("ALL");
  //     await revokeOtherSessions();
  //   } catch (error) {
  //     console.error(error);
  //     alert("Unable to revoke other sessions right now.");
  //   } finally {
  //     setLoading(null);
  //   }
  // }, [revokeOtherSessions]);

  const onContinueHere = useCallback(async () => {
    try {
      setLoading("CONTINUE");
      await revokeOtherSessions();
    } catch (error) {
      console.error(error);
      alert("Unable to end other sessions right now.");
    } finally {
      setLoading(null);
    }
  }, [revokeOtherSessions]);

  const onSwitchAccount = useCallback(async () => {
    try {
      setLoading("SWITCH");
      await signOut();
    } catch (error) {
      console.error(error);
      alert("Unable to switch accounts right now.");
    } finally {
      setLoading(null);
    }
  }, [signOut]);

  if (!currentUser || !current || others.length === 0) {
    return <>{children}</>;
  }

  return (
    <Gate
      current={current}
      others={others}
      onRevokeOne={onRevokeOne}
      // onRevokeAllOthers={onRevokeAllOthers}
      onContinueHere={onContinueHere}
      onSwitchAccount={onSwitchAccount}
      loading={loading}
    />
  );
}

export default SessionLimiter;
